// ---------------------------------------------------------------------------
// Visitor-segment selectors for the engagement endpoint (Admin / Non-admin /
// All). Pure data + functions only (no React, no DOM) so `node --test` can pin
// the default and the request serialization directly — mirroring
// lib/rangePresets.js and lib/insights.js.
//
// WHY THIS FILE EXISTS
// --------------------
// The Admin/Non-admin split is a SERVER-SIDE concept (functions/api/engagement.js
// `?segment=`) that only applies to the `publicark_engagement` dataset, whose
// blob6 holds the signed-in user id ('' = anonymous). Because the control lives
// in the global header it must persist across tab switches — so the option
// table, the default and the serialization are kept here as the single source of
// truth the context, the Header control and the API client all consume.
// ---------------------------------------------------------------------------

// Labels mirror the server's accepted values (`all` | `non-admin` | `admin`).
export const SEGMENT_OPTIONS = [
  { id: 'all', label: 'All', hint: 'Everyone, including the admin/owner' },
  { id: 'non-admin', label: 'Non-admin', hint: 'Excludes admin/owner traffic' },
  { id: 'admin', label: 'Admin', hint: 'Only admin/owner traffic' },
];

// WHY DEFAULT 'non-admin': this dashboard exists to show how REAL visitors
// behave. The owner's own browsing is noise — it inflates visitors/dwell/open
// counts and pollutes the artwork leaderboard — so the honest aggregate is the
// one with admin traffic removed. 'all' and 'admin' remain one click away when
// the owner deliberately wants to see their own effect or the raw whole.
export const DEFAULT_SEGMENT = 'non-admin';

// Coerce any (possibly stale/garbage) value to a valid segment id. Unknown ids
// fall back to the default so a bad value can never reach the API as a 400.
export function normalizeSegment(segment) {
  return SEGMENT_OPTIONS.some((o) => o.id === segment) ? segment : DEFAULT_SEGMENT;
}

// ---------------------------------------------------------------------------
// Tab → dataset capability map.
//
// Only the PublicArk Deep Dive reads `publicark_engagement` (which carries the
// user id in blob6) via /api/engagement, so it is the ONLY tab where a segment
// is meaningful. Every other tab — Freeform Explorer, 0mod Monetization and the
// Network Rollup — reads `metricsDataset` (overview/query/monetization), which
// has no user-id column at all; a segment CANNOT be applied there without
// fabricating a filter. The header control therefore disables itself on those
// tabs rather than silently no-op and mislead the reader.
// ---------------------------------------------------------------------------
export const SEGMENTABLE_TABS = new Set(['publicark']);

export function isSegmentableTab(tab) {
  return SEGMENTABLE_TABS.has(tab);
}

// Short inline hint shown next to the (disabled) control on non-segmentable
// tabs so the user is never misled into thinking it filtered those charts.
export const SEGMENT_SCOPE_HINT = 'Segment applies to engagement data';
